import app from 'app';
import Vue from 'vue';
import { workPool } from '@wochitnpm/helpers';
/**
 * Update specific asset with generated preview
 * stored in previewResultData field
 *
 * should replace AssetModel::createAssetPreview()
 */

/** @namespace workPool.job.createAssetPreview */
export default workPool.define(
  'createAssetPreview',
  {
    getContext() {
      return {
        assetId: this.options.asset && this.options.asset.id,
      };
    },

    initialize() {
      this.options.type = this.options.type ?? app.RealtimePreviewType.VIDEO;
    },

    beforeRun(asset) {
      Vue.$$set(asset, {
        // data source
        previewResultData: null,
        // progress relevant
        _previewOp: 'create',
        _previewStep: 0,
        _previewStartTime: Date.now(),
      });
    },

    getInstagramData(asset) {
      let promise;

      if (app.assetType.isINSTAGRAM(asset) && !asset._instagramProfileFetched) {
        promise = app.api.instagramExt
          .getUser({
            query: asset.metadata.socialUserScreenName,
          })
          .then(
            (user) => {
              Vue.$$set(asset, {
                _instagramProfileFetching: false,
                _instagramProfileFetched: true,
              });
              Vue.$$set(asset.metadata, {
                socialUserName: user.full_name,
                socialUserProfileImageUrl: user.profile_pic_url,
              });
              return asset;
            },
            () => {
              Vue.$$set(asset, {
                _instagramProfileFetching: false,
                _instagramProfileFetched: true,
              });
              return asset;
            }
          );
      } else {
        promise = Promise.resolve(asset);
      }
      return promise;
    },

    run() {
      const { asset, programmer } = this.options;

      this.beforeRun(asset);

      // this.getInstagramData(asset).then(() => {
      let duration = programmer.configInitialInfoGfxDT;
      if (app.assetType.isSOCIALFRAGMENT(asset)) {
        duration = asset.endTime - asset.startTime;
      }

      const postData =
        app.namespace.Controller.AssetController.serializeAsset(asset);
      // prd preview should only contain crop values, if any.
      if (!_.isEmpty(postData.agd)) {
        postData.agd = _.pick(postData.agd, ['scale', 'offset']);
      }

      Object.assign(postData, {
        // TODO: check why to bring timeline entities during social PRD?
        displayStartTime: 0,
        displayDuration: duration,
        displayEndTime: duration,

        volumeControl: {
          video: 1,
          bgMusic: null,
          narration: null,
        },
      });

      app.realTimePreviewController
        .addTrackToPreviewQueue({
          delay: 0,
          visualTrack: postData,
          silent: false,
          type: this.options.type || app.RealtimePreviewType.VIDEO,
          source: this.type, // TODO: find usecase (was 'assetModel')
          forceStoryAspectRatio: true,
        })
        .then((rtpModel) => {
          if (rtpModel.isDone()) {
            this.onDone(rtpModel);
          } else if (rtpModel.isFailed()) {
            this.onFailure();
          } else {
            rtpModel.on('change:status change:_lastTouch', () => {
              if (rtpModel.isDone()) {
                this.onDone(rtpModel);
              } else if (rtpModel.isFailed()) {
                this.onFailure();
              }
            });
          }
        })
        .fail(() => {
          this.onFailure();
        });
      // });
    },

    onDone(rtpModel) {
      if (!this._cpFinish.isPending()) {
        // aborted?
        return;
      }
      const asset = this.options.asset;

      const rtp = rtpModel.get('realTimePreviewResultData');
      app.prependHttpAcrossObject(rtp);
      // TODO: experiment
      // Vue.$$set(asset, {
      //   previewResultData: rtp,
      //   _previewOp: 'done',
      // });
      Vue.set(asset, '_previewOp', 'done');

      if (this.options.type === app.RealtimePreviewType.VIDEO) {
        Vue.set(asset, 'previewResultData', rtp);
      } else if (this.options.type === app.RealtimePreviewType.STATIC) {
        Vue.set(asset, '_staticPreview', rtp.cdnUrl);
      } else if (this.options.type === app.RealtimePreviewType.STATIC_MASK) {
        Vue.set(asset, '_staticMaskPreview', rtp.cdnUrl);
        console.log('_staticMaskPreview', rtp.cdnUrl);
      } else {
        app.doctorController.report(
          `createAssetPreview unknown type: ${this.options.type}`
        );
      }

      this.$emit({ asset });
      this.finish();
    },

    onFailure() {
      const asset = this.options.asset;

      Vue.$$set(asset, {
        previewResultData: null,
        _previewOp: 'fail',
      });

      this.$emit({ error: true, asset });
      this.abort();
    },

    abort() {
      const asset = this.options.asset;

      app.resetVisualTrackRTP(asset);
      this.$emit({ asset, abort: true });
      this._cpFinish.reject({ abort: true });
      this.options.asset = null;
    },
  },
  {
    forExternalAsset({ asset, programmer, type }) {
      const isRelevant =
        app.assetType.isSOCIAL(asset) ||
        app.assetType.isSOCIALFRAGMENT(asset) ||
        app.assetType.isMAP(asset);
      const isNeeded = isRelevant && !asset.previewResultData;
      isNeeded && workPool.add(new this({ asset, programmer, type }));
      return isNeeded;
    },

    forChangedAspectRatio({ asset, programmer }) {
      const isRelevant =
        app.assetType.isSOCIAL(asset) ||
        app.assetType.isSOCIALFRAGMENT(asset) ||
        app.assetType.isMAP(asset) ||
        app.assetType.isWEBPAGE(asset);

      if (workPool.job.createExternalAsset.isRunning(asset)) {
        return;
      }

      if (isRelevant) {
        // abort existing - any new prd request should prevail
        const runningJobs = workPool.get({
          type: this.type,
          context: (jc) => jc.assetId === asset.id,
        });

        _.each(runningJobs, (job) => {
          job.abort();
        });
        workPool.add(new this({ asset, programmer }));

        app.trigger('disable-draggable-asset', { asset });
      }
    },

    updateSelectedAsset(asset, selectedAssets) {
      const selectedOne = _.find(selectedAssets, { id: asset.id });
      if (selectedOne && asset !== selectedOne) {
        Vue.$$set(selectedOne, asset);
      }
    },
  }
);
